import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { NoDataItem } from '~/modules/common/components';
import PageOutOfBoundsMessage from '~/modules/common/components/ListTable/PageOutOfBoundsMessage';
import { useGetDefaultGroupField } from '~/modules/resource-management/ResourcingOverview/components/ResourceAvailabilityCard/hooks';
import { useResourceAvailabilityPermissions } from '~/modules/resource-management/ResourcingOverview/hooks';
import { SortDirection } from '~/types';
import { TableHeaderPagination } from '~/modules/resourcing/common/components';
import useResourceOverviewToolbarContext from '~/modules/resource-management/ResourceManagementDashboard/hooks/useResourceManagementDashboardContext';
import useResourceManagementContext from '~/modules/resource-management/common/context/useResourceManagementContext';
import useFilterBasedOnSearchCriteria from '~/modules/resourcing/hooks/useFilterBasedOnSearchCriteria';
import {
  useBenchOverAllocationChartAndTableData,
  DEFAULT_PAGE_SIZE
} from './hooks/useBenchOverAllocationChartAndTableData';
import {
  BenchOverAllocationTable,
  BenchOverAllocationTrackerToolbar,
  BenchOverAllocationChart,
  PROBLEM_FILTER_OPTIONS,
  DEFAULT_VIEW,
  TOGGLE_VIEW_OPTIONS
} from './components';

const useNavigationStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  }
}));

const useNoDataStyles = makeStyles(theme => ({
  root: {
    ...theme.typography.caption,
    fontStyle: 'normal',
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
    display: 'block',
    width: '100%'
  }
}));

const NoDataMessage = () => {
  const classes = useNoDataStyles();

  return (
    <NoDataItem classes={classes}>
      <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.noDataMessage" />
    </NoDataItem>
  );
};

export const BenchOverAllocationTrackerDetailsSection = () => {
  const {
    isViewRoleEnabled,
    isViewCostCenterEnabled,
    isViewDivisionEnabled,
    isViewDepartmentEnabled,
    isViewEmployeeTypeEnabled,
    isViewLocationEnabled,
    isViewServiceCenterEnabled
  } = useResourceAvailabilityPermissions();
  const navigationClasses = useNavigationStyles();

  const groupByField = useGetDefaultGroupField({
    isViewRoleEnabled,
    isViewCostCenterEnabled,
    isViewDivisionEnabled,
    isViewDepartmentEnabled,
    isViewEmployeeTypeEnabled,
    isViewLocationEnabled,
    isViewServiceCenterEnabled
  });

  const [groupBy, setGroupBy] = useState(groupByField);
  const [selectedProblem, setSelectedProblem] = useState(
    PROBLEM_FILTER_OPTIONS.BENCH
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState(DEFAULT_VIEW);
  const {
    dateRange,
    displayUnit: overviewUnit,
    periodScale
  } = useResourceOverviewToolbarContext();

  const onGroupByChange = useCallback(newValue => {
    setGroupBy(newValue);
    setCurrentPage(1);
  }, []);

  const onProblemFilterChange = useCallback(newValue => {
    setSelectedProblem(newValue);
    setCurrentPage(1);
  }, []);

  const { searchCriteria } = useResourceManagementContext();
  const { filter } = useFilterBasedOnSearchCriteria({ searchCriteria });

  const {
    availabilityData,
    loading,
    availabilityTotals,
    hasMoreRows
  } = useBenchOverAllocationChartAndTableData({
    dateRange,
    groupBy,
    filter,
    overviewUnit,
    periodScale,
    sort: { field: groupBy, direction: SortDirection.Asc },
    page: currentPage
  });

  const onViewChange = useCallback(newValue => {
    setSelectedView(newValue);
  }, []);

  return (
    <>
      <BenchOverAllocationTrackerToolbar
        groupBy={groupBy}
        onGroupByChange={onGroupByChange}
        selectedProblem={selectedProblem}
        onProblemFilterChange={onProblemFilterChange}
        selectedView={selectedView}
        onViewChange={onViewChange}
      />
      <TableHeaderPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={DEFAULT_PAGE_SIZE}
        isPastPaginationLimit={hasMoreRows}
        currentPageRowCount={availabilityData.length}
        classes={navigationClasses}
      />
      {!loading && availabilityData.length === 0 ? (
        currentPage > 1 ? (
          <PageOutOfBoundsMessage
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        ) : (
          <NoDataMessage />
        )
      ) : selectedView === TOGGLE_VIEW_OPTIONS.TABLE ? (
        <BenchOverAllocationTable
          groupBy={groupBy}
          rows={availabilityData}
          totals={availabilityTotals}
          selectedProblem={selectedProblem}
          loading={loading}
          hasMoreRows={hasMoreRows}
          overviewUnit={overviewUnit}
        />
      ) : (
        <BenchOverAllocationChart
          availabilityData={availabilityData}
          loading={loading}
          selectedProblem={selectedProblem}
          groupBy={groupBy}
        />
      )}
    </>
  );
};

export default BenchOverAllocationTrackerDetailsSection;
